import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  PageWidth,
} from '@laerdal/life-react-components';
import { selectMarket, buildMarketPath } from '../../store/slices/globalConfigSlice';
import { useNavigate } from 'react-router-dom';
import {
  Step0,
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
  Step6,
  Step7,
  Step8,
  Step9,
  Step10,
  StepResolved
} from './components';
import posthog from 'posthog-js';

const QcprSupportContainer = styled.div`
  margin: 0 0 32px 0;
  gap: 32px;
  display: flex;
  flex-direction: column;
`;

const PageContainer = styled.div`
  padding: 12px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 32px;
`;



const QcprSupportTroubleshootingPage = () => {
  const { t } = useTranslation(['translation', 'qcprSupport']);
  const currentMarket = useSelector(selectMarket);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [currentCategory, setCurrentCategory] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [firmwareVersion, setFirmwareVersion] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [deviceType, setDeviceType] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [operatingSystem, setOperatingSystem] = useState('');
  const [operatingSystemVersion, setOperatingSystemVersion] = useState('');
  const [caseNumber, setCaseNumber] = useState('');

  useEffect(() => {
    // Track troubleshooting page visit
    posthog.capture('SUPP-QcprSupport-TroubleshootingPageVisit', {
      page: 'QcprSupportTroubleshooting',
      step: currentStep,
      market: currentMarket
    });
  }, [currentMarket]);

  const handleNextStep = (step?: number) => {
    const nextStep = step || currentStep + 1;

    posthog.capture('SUPP-QcprSupport-TroubleshootingStepNext', {
      from_step: currentStep,
      to_step: nextStep,
      category: currentCategory,
      market: currentMarket
    });

    window.scrollTo({ top: 0, behavior: 'auto' });
    if (step) {
      setCurrentStep(step);
      posthog.capture(`SUPP-QcprSupport-TroubleshootingStep-${step}`);
    } else if (currentStep < 12) {
      setCurrentStep(currentStep + 1);
      posthog.capture(`SUPP-QcprSupport-TroubleshootingStep-${currentStep + 1}`);
    } else {
      // Finished all steps, navigate back or to success page
      navigate(buildMarketPath(currentMarket, '/qcpr-support'));
    }
  };

  const handlePreviousStep = () => {
    posthog.capture('SUPP-QcprSupport-TroubleshootingStepBack', {
      from_step: currentStep,
      to_step: showResolved ? currentStep : currentStep - 1,
      category: currentCategory,
      market: currentMarket
    });

    window.scrollTo({ top: 0, behavior: 'auto' });
    if (showResolved) {
      setShowResolved(false)
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      posthog.capture(`SUPP-QcprSupport-TroubleshootingStep-${currentStep - 1}`);
    }

    if (currentStep == 0)
      navigate(buildMarketPath(currentMarket, '/qcpr-support'));
  };

  const handleResolvedStep = () => {
    posthog.capture('SUPP-QcprSupport-TroubleshootingIssueResolved', {
      step: currentStep,
      category: currentCategory,
      app_version: appVersion,
      firmware_version: firmwareVersion,
      market: currentMarket
    });
    setShowResolved(true);
  };

  const handleCategoryChange = (category: string) => {
    posthog.capture('SUPP-QcprSupport-TroubleshootingCategorySelected', {
      category: category,
      step: currentStep,
      market: currentMarket
    });
    setCurrentCategory(category);
  };


  const renderCurrentStep = () => {
    if (showResolved)
      return <StepResolved onPrevious={handlePreviousStep} />;

    switch (currentStep) {
      case 0:
        return <Step0 onNext={handleNextStep} handleCategoryChange={handleCategoryChange} onPrevious={handlePreviousStep} />;
      case 1:
        return <Step1 onNext={handleNextStep} handleResolvedStep={handleResolvedStep} setAppVersion={(version) => setAppVersion(version)} onPrevious={handlePreviousStep} />;
      case 2:
        return <Step2 onNext={handleNextStep} onPrevious={handlePreviousStep} handleResolvedStep={handleResolvedStep} />;
      case 3:
        return <Step3 onNext={handleNextStep} onPrevious={handlePreviousStep} handleResolvedStep={handleResolvedStep} />;
      case 4:
        return <Step4 onNext={handleNextStep} onPrevious={handlePreviousStep} handleResolvedStep={handleResolvedStep} />;
      case 5:
        return <Step5 onNext={handleNextStep} onPrevious={handlePreviousStep} handleResolvedStep={handleResolvedStep} />;
      case 6:
        return <Step6 onNext={handleNextStep} onPrevious={handlePreviousStep} handleResolvedStep={handleResolvedStep} setSelectedProducts={(products: string[]) => setSelectedProducts(products)} setAttachedFiles={(files: File[]) => setAttachedFiles(files)} />;
      case 7:
        return <Step7 onNext={handleNextStep} onPrevious={handlePreviousStep} setFirmwareVersion={(version: string) => setFirmwareVersion(version)} handleResolvedStep={handleResolvedStep} />;
      case 8:
        return <Step8 onNext={handleNextStep} onPrevious={handlePreviousStep} handleResolvedStep={handleResolvedStep} setDeviceType={(type: string) => setDeviceType(type)} setDeviceModel={(model: string) => setDeviceModel(model)} setOperatingSystem={(os: string) => setOperatingSystem(os)} setOperatingSystemVersion={(version: string) => setOperatingSystemVersion(version)} />;
      case 9:
        return <Step9
          onNext={handleNextStep}
          onPrevious={handlePreviousStep}
          handleResolvedStep={handleResolvedStep}
          currentStep={currentStep}
          showResolved={showResolved}
          currentCategory={currentCategory}
          appVersion={appVersion}
          firmwareVersion={firmwareVersion}
          selectedProducts={selectedProducts}
          attachedFiles={attachedFiles}
          deviceType={deviceType}
          deviceModel={deviceModel}
          operatingSystem={operatingSystem}
          operatingSystemVersion={operatingSystemVersion}
          setCaseNumber={(caseNumber: string) => setCaseNumber(caseNumber)}
        />;
      case 10:
        return <Step10 caseNumber={caseNumber} category={currentCategory} />;
    }
  };

  return (
    <>
      <PageWidth $useMaxWidth={true} $maxWidth={1024}>
        <PageContainer>
          <QcprSupportContainer>
            {renderCurrentStep()}
          </QcprSupportContainer>
        </PageContainer>
      </PageWidth>
    </>
  );
};

export default QcprSupportTroubleshootingPage;
